import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/lib/utils';
import axios from 'axios';
import { CheckCircle2, CreditCard, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function Payment() {
  const { orderId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentType, setPaymentType] = useState('full'); // 'full' or 'advance'

  useEffect(() => {
    fetchOrder();
    loadRazorpayScript();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      let response;
      
      // Try authenticated endpoint first if user is logged in
      if (token) {
        try {
          response = await axios.get(`${API_URL}/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (authError) {
          // If authenticated request fails, try guest endpoint
          response = await axios.get(`${API_URL}/orders/guest/${orderId}`);
        }
      } else {
        // No token, use guest endpoint
        response = await axios.get(`${API_URL}/orders/guest/${orderId}`);
      }
      
      setOrder(response.data);
      setPaymentStatus(response.data.payment_status);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order. Please try again.');
      // Don't redirect to profile for guests - go back to cart
      navigate(token ? '/profile' : '/cart');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentAmount = () => {
    if (!order) return 0;
    return paymentType === 'advance' ? Math.round(order.total / 2) : order.total;
  };

  const handleOnlinePayment = useCallback(async () => {
    setPaymentLoading(true);
    
    try {
      // Determine if this is a guest order
      const isGuestOrder = order?.is_guest || !token;
      const endpoint = isGuestOrder ? `${API_URL}/payment/guest/create-order` : `${API_URL}/payment/create-order`;
      const headers = isGuestOrder ? {} : { Authorization: `Bearer ${token}` };
      
      // Create Razorpay order with payment type
      const orderResponse = await axios.post(
        endpoint,
        { order_id: orderId, payment_type: paymentType },
        { headers }
      );

      const { razorpay_order_id, razorpay_key_id, amount, currency, payment_description } = orderResponse.data;

      // Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount: amount,
        currency: currency,
        name: 'Modular Furniture Store',
        description: payment_description || `Order #${orderId.substring(0, 8)}`,
        image: 'https://customer-assets.emergentagent.com/job_adae0dda-f495-46ee-835c-c691ea3f4474/artifacts/3rjauci5_LOGO.png',
        order_id: razorpay_order_id,
        handler: async function (response) {
          // Verify payment on backend
          try {
            const verifyEndpoint = isGuestOrder ? `${API_URL}/payment/guest/verify` : `${API_URL}/payment/verify`;
            const verifyResponse = await axios.post(
              verifyEndpoint,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: orderId,
                payment_type: paymentType
              },
              { headers }
            );

            if (verifyResponse.data.success) {
              // Track Google Ads Purchase Conversion
              if (typeof window.gtag === 'function') {
                window.gtag('event', 'conversion', {
                  'send_to': 'AW-17779376991/purchase',
                  'value': order?.total_amount || 0,
                  'currency': 'INR',
                  'transaction_id': orderId
                });
              }
              
              if (paymentType === 'advance') {
                toast.success('50% Advance payment successful! Balance due before dispatch.');
                setPaymentStatus('advance_paid');
                // Redirect to thank you page with advance payment info
                navigate(`/thank-you/${orderId}?payment=advance`);
              } else {
                toast.success('Payment successful!');
                setPaymentStatus('paid');
                navigate(`/thank-you/${orderId}`);
              }
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: order?.buyer_details?.name || user?.name || '',
          email: order?.buyer_details?.email || user?.email || '',
          contact: order?.buyer_details?.contact_number || ''
        },
        notes: {
          order_id: orderId,
          payment_type: paymentType
        },
        theme: {
          color: '#1a1a1a'
        },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setPaymentLoading(false);
      });

      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  }, [orderId, token, order, user, navigate, paymentType]);

  if (loading) {
    return (
      <div className="min-h-screen noise-texture">
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-20 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-xl uppercase tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  // If already paid, show success message
  if (paymentStatus === 'paid') {
    return (
      <div className="min-h-screen noise-texture">
        <PremiumHeader />
        <div className="container mx-auto px-4 sm:px-8 py-12 max-w-4xl">
          <div className="bg-white border border-border rounded-none p-12 text-center">
            <CheckCircle2 className="w-32 h-32 mx-auto mb-6 text-green-600 stroke-[2.5]" />
            <h1 className="text-4xl md:text-5xl font-black uppercase mb-4">PAYMENT COMPLETE!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Your payment has been successfully processed.
            </p>
            <Link to={`/thank-you/${order.id}`}>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wider font-bold px-12 py-6 text-lg">
                VIEW ORDER CONFIRMATION
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-texture">
      <PremiumHeader />

      <div className="container mx-auto px-4 sm:px-8 py-12 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Previous Page
        </button>

        <div className="bg-white border border-border rounded-none p-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-black uppercase mb-4">COMPLETE YOUR PAYMENT</h1>
            <p className="text-lg text-muted-foreground">
              Order ID: <span className="mono font-bold" data-testid="order-id">{order.id}</span>
            </p>
          </div>

          {/* Order Summary */}
          <div className="border-t border-b border-border py-8 mb-8">
            <h2 className="text-2xl font-black uppercase mb-6">ORDER SUMMARY</h2>
            <div className="space-y-3 mono text-sm">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-bold">{formatPrice(order.subtotal)}</span>
              </div>
              {order.discount_percent > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({order.discount_percent}%):</span>
                  <span className="font-bold">-{formatPrice(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>CGST (9%):</span>
                <span className="font-bold">{formatPrice(order.cgst)}</span>
              </div>
              <div className="flex justify-between">
                <span>SGST (9%):</span>
                <span className="font-bold">{formatPrice(order.sgst)}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery Charges:</span>
                <span className="font-bold">{formatPrice(order.delivery_charges)}</span>
              </div>
              <div className="border-t border-border pt-3 mt-3">
                <div className="flex justify-between text-xl">
                  <span className="font-bold">TOTAL AMOUNT:</span>
                  <span className="font-black text-primary" data-testid="payment-total">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Type Selection - Split Payment Option */}
          <div className="mb-8">
            <h2 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
              <span className="text-2xl">💳</span> Choose Payment Plan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Payment Option */}
              <div 
                onClick={() => setPaymentType('full')}
                className={`cursor-pointer p-6 border-2 rounded-lg transition-all ${
                  paymentType === 'full' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentType === 'full' ? 'border-primary' : 'border-gray-300'
                  }`}>
                    {paymentType === 'full' && <div className="w-3 h-3 rounded-full bg-primary"></div>}
                  </div>
                  <span className="font-bold text-lg">Pay Full Amount</span>
                </div>
                <p className="text-2xl font-black text-primary mb-2">{formatPrice(order.total)}</p>
                <p className="text-sm text-muted-foreground">Pay 100% now and we start processing immediately</p>
              </div>

              {/* 50% Advance Option */}
              <div 
                onClick={() => setPaymentType('advance')}
                className={`cursor-pointer p-6 border-2 rounded-lg transition-all relative overflow-hidden ${
                  paymentType === 'advance' 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Recommended Badge */}
                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1">
                  RECOMMENDED
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    paymentType === 'advance' ? 'border-emerald-500' : 'border-gray-300'
                  }`}>
                    {paymentType === 'advance' && <div className="w-3 h-3 rounded-full bg-emerald-500"></div>}
                  </div>
                  <span className="font-bold text-lg">Pay 50% Advance</span>
                </div>
                <p className="text-2xl font-black text-emerald-600 mb-2">{formatPrice(Math.round(order.total / 2))}</p>
                <p className="text-sm text-muted-foreground mb-2">
                  Pay 50% now, balance <span className="font-bold">{formatPrice(Math.round(order.total / 2))}</span> before dispatch
                </p>
                <div className="bg-emerald-100 text-emerald-800 text-xs px-3 py-2 rounded mt-2">
                  🛡️ You&apos;ll receive live photos/video of your packed order before paying balance
                </div>
              </div>
            </div>

            {paymentType === 'advance' && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                <p className="font-bold text-amber-800 mb-2">📋 How Split Payment Works:</p>
                <ol className="list-decimal list-inside text-amber-700 space-y-1">
                  <li>Pay 50% advance now to confirm your order</li>
                  <li>We manufacture your furniture (7-15 days)</li>
                  <li>You receive photos/video of packed material</li>
                  <li>Pay remaining 50% before dispatch</li>
                  <li>Material dispatched within 24 hours of balance payment</li>
                </ol>
              </div>
            )}
          </div>

          {/* Online Payment Button - Primary CTA */}
          <div className="mb-12">
            <div className={`border-2 rounded-none p-8 text-center ${
              paymentType === 'advance' ? 'bg-emerald-50 border-emerald-500' : 'bg-primary/5 border-primary'
            }`}>
              <CreditCard className={`w-16 h-16 mx-auto mb-4 ${paymentType === 'advance' ? 'text-emerald-600' : 'text-primary'}`} />
              <h2 className="text-2xl font-black uppercase mb-4">
                {paymentType === 'advance' ? 'PAY 50% ADVANCE' : 'PAY FULL AMOUNT'}
              </h2>
              <p className="text-muted-foreground mb-6">
                Pay securely using UPI, Credit/Debit Card, Net Banking, or Wallets
              </p>
              <Button
                onClick={handleOnlinePayment}
                disabled={paymentLoading}
                className={`rounded-none uppercase tracking-wider font-bold px-12 py-6 text-lg w-full md:w-auto ${
                  paymentType === 'advance' 
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    PROCESSING...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    PAY {formatPrice(getPaymentAmount())} NOW
                  </>
                )}
              </Button>
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-muted-foreground">
                <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" className="h-6" />
                <span>Secured by Razorpay</span>
              </div>

              {/* Safe-Buy Guarantee Block */}
              <div className="mt-8 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <h3 className="font-black text-lg text-emerald-800 uppercase">Our 100% Delivery & Quality Guarantee</h3>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="text-emerald-600 font-bold mt-0.5">🛡️</span>
                    <div>
                      <span className="font-bold text-foreground">Legacy Protection:</span>
                      <span className="text-muted-foreground"> Backed by Ezeefit&apos;s 10-year track record of successful office installations.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-emerald-600 font-bold mt-0.5">🚛</span>
                    <div>
                      <span className="font-bold text-foreground">Safe Transit:</span>
                      <span className="text-muted-foreground"> Every order is fully insured. If it arrives damaged, we replace it at zero cost to you.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-emerald-600 font-bold mt-0.5">📸</span>
                    <div>
                      <span className="font-bold text-foreground">Post-Pay Verification:</span>
                      <span className="text-muted-foreground"> Receive a Live Photo or Video of your packed order from our factory before it ships.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-emerald-600 font-bold mt-0.5">✅</span>
                    <div>
                      <span className="font-bold text-foreground">Manufacturer Warranty:</span>
                      <span className="text-muted-foreground"> Your investment is covered by a 1-Year Structural Warranty.</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <span className="text-emerald-600 font-bold mt-0.5">📄</span>
                    <div>
                      <span className="font-bold text-foreground">B2B Verified:</span>
                      <span className="text-muted-foreground"> Need a formal invoice? Download your GST-Ready Proforma instantly for your accounts team.</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-emerald-200 flex items-center justify-center gap-2 text-xs text-emerald-700">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modshop-seo/artifacts/us9gplcj_image.png" 
                    alt="Ezeefit" 
                    className="h-6 w-auto"
                  />
                  <span className="font-medium">A Division of Ezeefit – Trusted Since 2014</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
